import { useState } from 'react';
import type { AgentStep } from '@shared/types';
import {
  Brain,
  ChevronDown,
  ChevronRight,
  Wrench,
  Check,
  X,
  AlertCircle,
  GitCommit,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { ValidationObservation } from '@/components/chat/ValidationObservation';

interface Props {
  steps: AgentStep[];
}

/** Render the per-turn agent activity as a chronological stack of cards.
 *  Mirrors the `steps` array order verbatim — the server is responsible for
 *  ordering. */
export function AgentTimeline({ steps }: Props) {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      {steps.map((step) => (
        <StepCard key={`${step.type}-${step.id}`} step={step} />
      ))}
    </div>
  );
}

function StepCard({ step }: { step: AgentStep }) {
  switch (step.type) {
    case 'thinking':
      return <ThinkingCard text={step.text} />;
    case 'text':
      return <TextCard text={step.text} />;
    case 'tool_use':
      // Don't render the tool_use card for ask_user — the question prompt at
      // the bottom of the message already conveys it. Avoids duplicate UI.
      if (step.name === 'ask_user') return null;
      return <ToolUseCard name={step.name} input={step.input} />;
    case 'tool_result':
      // Same reasoning — the question prompt UI is the canonical surface.
      if (step.toolName === 'ask_user') return null;
      return (
        <ToolResultCard
          toolName={step.toolName}
          ok={step.ok}
          observation={step.observation}
        />
      );
    case 'checkpoint':
      // Skip empty checkpoints — they happen when a turn ends after only
      // an ask_user / set_parameter call with no real geometry change yet.
      if (step.partCount === 0 && step.jointCount === 0) return null;
      return (
        <CheckpointCard
          partCount={step.partCount}
          jointCount={step.jointCount}
          featureCount={step.featureCount}
          parameterCount={step.parameterCount}
          specHashShort={step.specHashShort}
          summary={step.summary}
        />
      );
    case 'error':
      return <ErrorCard message={step.message} />;
    default:
      return null;
  }
}

// ─── Cards ────────────────────────────────────────────────────────────────

function ThinkingCard({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('common');
  if (!text.trim()) return null;
  return (
    <div className="rounded-lg border-2 border-brand-indigo/15 bg-k-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-xs font-semibold text-brand-indigo/70 hover:text-brand-indigo"
      >
        <Brain className="h-3.5 w-3.5 text-brand-indigo shrink-0" />
        <span className="flex-1 text-left">{t('thinking')}</span>
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
      </button>
      {open && (
        <div className="px-2.5 pb-2">
          <pre className="max-h-60 overflow-y-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-brand-indigo/70">
            {text}
          </pre>
        </div>
      )}
    </div>
  );
}

function TextCard({ text }: { text: string }) {
  if (!text.trim()) return null;
  return (
    <div className="px-1 text-sm text-synergy-text-primary whitespace-pre-wrap break-words">
      {text}
    </div>
  );
}

function ToolUseCard({
  name,
  input,
}: {
  name: string;
  input: Record<string, unknown>;
}) {
  const summary = summarizeToolInput(name, input);
  const { t } = useTranslation('common');
  return (
    <div className="flex items-start gap-2 rounded-lg border-2 border-brand-indigo/15 bg-brand-cream/60 px-2.5 py-1.5 text-xs">
      <Wrench className="h-3.5 w-3.5 text-brand-indigo shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-brand-indigo">
          {t(`toolNames.${name}`, { defaultValue: name })}
        </div>
        {summary && (
          <div className="text-brand-indigo/70 break-all whitespace-pre-wrap">
            {summary}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolResultCard({
  toolName,
  ok,
  observation,
}: {
  toolName: string;
  ok: boolean;
  observation: string;
}) {
  const [open, setOpen] = useState(!ok);
  const firstLine = observation.split('\n')[0] || '';
  return (
    <div
      className={cn(
        'rounded-md border px-2.5 py-1.5 text-xs',
        ok
          ? 'border-brand-teal/50 bg-brand-teal/15'
          : 'border-brand-indigo/40 bg-brand-cream',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 text-left"
      >
        {ok ? (
          <Check className="h-3.5 w-3.5 text-brand-indigo shrink-0 mt-0.5" strokeWidth={3} />
        ) : (
          <X className="h-3.5 w-3.5 text-brand-indigo shrink-0 mt-0.5" strokeWidth={3} />
        )}
        <div className="flex-1 min-w-0">
          <span className="block break-words font-semibold text-brand-indigo">
            {firstLine}
          </span>
        </div>
        {observation.length > firstLine.length && (
          open ? (
            <ChevronDown className="h-3 w-3 mt-0.5 text-brand-indigo/60 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 mt-0.5 text-brand-indigo/60 shrink-0" />
          )
        )}
      </button>
      {open && observation.length > firstLine.length && (
        <div className="mt-2">
          {observation.includes('validation_failed') || /\b(errors?|warnings?|infos?)\s*\(\d+\):/i.test(observation) ? (
            <ValidationObservation observation={observation} />
          ) : (
            <pre className="whitespace-pre-wrap break-words text-brand-indigo/70 text-[11px] leading-relaxed">
              {observation}
            </pre>
          )}
        </div>
      )}
      {open && (
        <div className="mt-1 text-[10px] text-brand-indigo/50 font-mono break-all">
          ← {toolName}
        </div>
      )}
    </div>
  );
}

function CheckpointCard({
  partCount,
  jointCount,
  featureCount,
  parameterCount,
  specHashShort,
  summary,
}: {
  partCount: number;
  jointCount: number;
  featureCount?: number;
  parameterCount?: number;
  specHashShort?: string;
  summary: string;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-start gap-2 rounded-md border-2 border-brand-indigo/30 bg-brand-teal-soft/30 px-2.5 py-1.5 text-xs">
      <GitCommit className="h-3.5 w-3.5 text-brand-indigo shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-semibold text-brand-indigo">
          <span>
            {t('checkpointSaved')} · {partCount}
            {t('parts')} / {jointCount}
            {t('joints')}
          </span>
          {(featureCount != null || parameterCount != null) && (
            <span className="text-brand-indigo/70">
              {featureCount != null ? `· ${featureCount}f` : ''}
              {parameterCount != null ? ` · ${parameterCount}p` : ''}
            </span>
          )}
          {specHashShort && (
            <span
              className="rounded bg-brand-indigo px-1.5 py-0.5 font-mono text-[10px] tracking-wider text-brand-cream"
              title={t('checkpointHashTooltip', { defaultValue: 'Spec fingerprint — differs whenever the saved spec changes' })}
            >
              #{specHashShort}
            </span>
          )}
        </div>
        <div className="break-words text-brand-indigo/70">{summary}</div>
      </div>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border-2 border-brand-indigo bg-brand-cream px-2.5 py-1.5 text-xs">
      <AlertCircle className="h-3.5 w-3.5 text-brand-indigo shrink-0 mt-0.5" strokeWidth={2.5} />
      <div className="flex-1 min-w-0 font-semibold text-brand-indigo whitespace-pre-wrap break-words">
        {message}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function summarizeToolInput(
  name: string,
  input: Record<string, unknown>,
): string {
  try {
    if (name === 'add_part' && typeof input.partJson === 'string') {
      const p = JSON.parse(input.partJson) as {
        id?: string;
        kind?: string;
        name?: string;
      };
      return `${p.kind ?? '?'} · ${p.id ?? '?'}${p.name ? ` (${p.name})` : ''}`;
    }
    if (name === 'add_joint' && typeof input.jointJson === 'string') {
      const j = JSON.parse(input.jointJson) as {
        id?: string;
        type?: string;
        fromPartId?: string;
        toPartId?: string;
      };
      return `${j.type ?? '?'} · ${j.fromPartId} ↔ ${j.toPartId}`;
    }
    if (name === 'modify_part' || name === 'modify_joint') {
      return `${input.partId ?? input.jointId ?? ''} ← ${String(input.patchJson ?? '').slice(0, 80)}`;
    }
    if (name === 'remove_part') return String(input.partId ?? '');
    if (name === 'remove_joint') return String(input.jointId ?? '');
    if (name === 'render') return input.force ? 'force=true' : '';
    if (name === 'ask_user' && typeof input.question === 'string') {
      return input.question.slice(0, 100);
    }
    // Fallback: JSON preview
    return JSON.stringify(input).slice(0, 80);
  } catch {
    return '';
  }
}
